import 'braid-design-system/reset';

import { HirerAuthProvider } from '@seek/hirer-auth-react';
import { HirerApolloProvider } from '@seek/hirer-graphql-react';
import { HubbleProvider } from '@seek/hubble';
import type { Locale } from '@seek/melways-sites';
import { useIsNVLEnabled } from '@seek/uiv-banner';
import { VocabProvider } from '@vocab/react';
import { BraidProvider, ToastProvider } from 'braid-design-system';
import apacTheme from 'braid-design-system/themes/apac';
import seekJobs from 'braid-design-system/themes/seekJobs';
import { StrictMode } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { Outlet } from 'react-router';

import {
  ConfigProvider,
  type ConfigOptions,
} from 'src/app/shared/contexts/configProvider';
import { apolloConfig } from 'src/app/utils/apolloClient';
import { isDev, isEnvMocked } from 'src/app/utils/environment';
import { hubble } from 'src/app/v1/analytics/hubble';
import { store } from 'src/app/v1/state/store';
import type { EmployerSiteName } from 'src/types/app';

export interface AppProps {
  config: ConfigOptions;
  language: Locale;
  site: EmployerSiteName;
}

export const App = ({ config, language }: AppProps) => {
  // The below is required to be client side during NVL development
  // as we don't want the server to render NVL and potentially cache and serve that to users.
  // NOTE: It does not cause additional rerenders if the theme remains unchanged which for all production
  // users will be the case as they wouldn't use the nvl query param
  const isNVLEnabled = useIsNVLEnabled(false);
  return (
    <StrictMode>
      <BraidProvider theme={isNVLEnabled ? seekJobs : apacTheme}>
        <HubbleProvider hubble={hubble}>
          <ToastProvider>
            <HirerAuthProvider useMockedAuth={isDev() && isEnvMocked()}>
              <HirerApolloProvider {...apolloConfig}>
                {/* TO-DO: Redux to be removed in future RTB */}
                <ReduxProvider store={store}>
                  <ConfigProvider config={config}>
                    <VocabProvider language={language}>
                      <Outlet />
                    </VocabProvider>
                  </ConfigProvider>
                </ReduxProvider>
              </HirerApolloProvider>
            </HirerAuthProvider>
          </ToastProvider>
        </HubbleProvider>
      </BraidProvider>
    </StrictMode>
  );
};
export default App;
